"use client";

/**
 * P2-04 · <SwatchRow> — up to N colour swatches, the rest collapsed into
 * "+X more". Per P2-DB04 r2 §03 every control is a real <button> layered
 * above the card link (never a nested anchor): swatches preview a variant on
 * hover, keyboard focus, or first tap; "+X more" is the one control that
 * navigates (to the PDP, where the full variant set lives).
 *
 * P18-19 · The row is the card's only *writer* of the preview state, so it
 * publishes through `useCardPreview()` instead of an `onPreview` prop, and it
 * asks the `cardSwatches` flag itself rather than being gated by the caller.
 * Both moves exist so the card's shell needs no hooks and can render on the
 * server; this file is the island.
 */
import { useRouter } from "next/navigation";

import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { CARD_MAX_SWATCHES } from "~/config/storefront";
import { useLocaleHref } from "~/i18n/provider";
import { swatchBackground } from "~/lib/colour-swatch";
import { cn } from "~/lib/utils";

import { useCardPreview } from "./card-preview";
import type { ProductCardViewSwatch } from "./types";

export interface SwatchRowProps {
  swatches: ProductCardViewSwatch[];
  /** PDP href the "+X more" overflow button navigates to. */
  href: string;
  maxSwatches?: number;
  className?: string;
}

export function SwatchRow(props: SwatchRowProps) {
  const enabled = useOptionalFeatureFlag("cardSwatches");
  // The gate sits in a wrapper so a store with the flag off — or a product with
  // no colours — mounts nothing at all, exactly as when the card decided. The
  // row's own hooks (the router, the preview) then only run where it renders.
  if (!enabled || props.swatches.length === 0) return null;
  return <SwatchRowControl {...props} />;
}

function SwatchRowControl({
  swatches,
  href,
  maxSwatches = CARD_MAX_SWATCHES,
  className,
}: SwatchRowProps) {
  const router = useRouter();
  // P8-01C · `href` is an unprefixed PDP path; keep the shopper in their language.
  const localeHref = useLocaleHref();
  const { preview, setPreview } = useCardPreview();

  const activeValue = preview?.value ?? null;
  const visible = swatches.slice(0, maxSwatches);
  const moreCount = swatches.length - visible.length;

  return (
    <div
      className={cn("relative z-[2] -my-1.5 -ml-1.5 flex items-center", className)}
      onMouseLeave={() => setPreview(null)}
      onBlur={(e) => {
        // Preview is transient — reset when focus leaves the whole row.
        if (!e.currentTarget.contains(e.relatedTarget)) setPreview(null);
      }}
    >
      {visible.map((swatch) => {
        const fill = swatchBackground(swatch.value, swatch.hex);
        return (
          <button
            key={swatch.value}
            type="button"
            aria-label={`Preview colour ${swatch.value}`}
            onMouseEnter={() => setPreview(swatch)}
            onFocus={() => setPreview(swatch)}
            onClick={() => setPreview(swatch)}
            // QA-CA · The button itself is the 32px hit area; the coloured
            // dot is a child, so the target is real rather than a pseudo-element.
            className="relative inline-flex size-8 items-center justify-center rounded-full focus-visible:outline-none"
          >
            <span
              aria-hidden="true"
              className={cn(
                "relative size-5 rounded-full border border-border",
                activeValue === swatch.value &&
                  "ring-2 ring-[var(--ring)] ring-offset-1 ring-offset-surface",
              )}
              style={fill ? { background: fill } : undefined}
            >
              {!fill && (
                <span className="absolute inset-0 rounded-full bg-surface-2" />
              )}
            </span>
          </button>
        );
      })}
      {moreCount > 0 && (
        <button
          type="button"
          aria-label={`${moreCount} more colours — view product`}
          onClick={() => router.push(localeHref(href))}
          className={cn(
            "relative inline-flex min-h-8 items-center px-1 text-xs font-medium text-fg-muted",
            "hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-1",
          )}
        >
          +{moreCount} more
        </button>
      )}
    </div>
  );
}
